/*
 * Lae bracelet campaign builder (templates/product.bracelet-paid-social.json only).
 * One state object drives every view of the design: tiles, strand, summary, prices, sticky bar.
 * Cart contract matches the original builder: Size, Gemstones "Name (n), ...", Special instructions,
 * plus the optional additive "Stone order" bead sequence (2+ stones).
 */
import { CartAddEvent } from '@theme/events';

const TEMPLATE_ID = 'product.bracelet-paid-social';
const REQUEST_TIMEOUT = 15000;

// Display only: keeps "(style A)" on one line. Cart values and announcements use the plain label.
const keepStyle = (label) => label.replace(/\(style (\w+)\)/, '(style $1)');
// How 'Your stones' is drawn for each style, matching the style photos. The section setting maps each
// style to one of these; unlisted styles are worked out from their name.
const LAYOUTS = new Set(['beaded', 'beaded-heart', 'bars-either-side', 'bars-either-side-heart', 'bars-between']);
// Both 'bars-between' photos (silver E, gold D) show four stone positions with a bar between each.
const BARS_BETWEEN_POSITIONS = 4;
const parseLayouts = (text) => {
  const map = new Map();
  for (const line of String(text || '').split(/\r?\n/)) {
    const at = line.lastIndexOf('=');
    if (at < 0) continue;
    const layout = line.slice(at + 1).trim().toLowerCase();
    if (LAYOUTS.has(layout)) map.set(line.slice(0, at).trim().toLowerCase(), layout);
  }
  return map;
};
const guessLayout = (label) => {
  const name = label.replace(/\s*\(style[^)]*\)/i, '');
  const bar = /\bbars?\b/i.test(name);
  const heart = /\bheart\b/i.test(name);
  if (bar) return heart ? 'bars-either-side-heart' : 'bars-either-side';
  return heart ? 'beaded-heart' : 'beaded';
};
// Write to the DOM only when a value changes: every tap re-renders about 50 tiles.
const setText = (el, text) => {
  if (el.textContent !== text) el.textContent = text;
};
const setAttr = (el, name, value) => {
  if (el.getAttribute(name) !== value) el.setAttribute(name, value);
};
const setHidden = (el, hidden) => {
  if (el.hidden !== hidden) el.hidden = hidden;
};
const listText = (items) => (items.length < 2 ? items[0] : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);
// True for keyboard focus and text fields, false after a tap or click. Browsers without :focus-visible say true.
const isFocusVisible = (el) => {
  try {
    return el.matches(':focus-visible');
  } catch (_error) {
    return true;
  }
};
/*
 * Motion: only where it shows what changed, never on page load or on text being read. Transform and
 * opacity only (the compositor runs them, so taps stay quick on slow phones), and none at all when
 * reduced motion is on. The CSS uses the same curves (--lae-ease, --lae-spring).
 */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
const canAnimate = () => !reduceMotion.matches && typeof Element.prototype.animate === 'function';
const animate = (el, keyframes, options) => {
  if (!el || !canAnimate()) return null;
  try {
    return el.animate(keyframes, options);
  } catch (_error) {
    return null;
  }
};
const FADE_UP = [
  { opacity: 0, transform: 'translateY(5px)' },
  { opacity: 1, transform: 'none' },
];
const BUMP = [{ transform: 'scale(1.3)' }, { transform: 'none' }];
// A stone settling onto the strand: a smaller give than a count's bump.
const LAND = [{ transform: 'scale(1.15)' }, { transform: 'none' }];
const BEAD_IN = [
  { opacity: 0, transform: 'translateY(-12px) scale(0.6)' },
  { opacity: 1, transform: 'translateY(1px) scale(1.06)', offset: 0.6 },
  { opacity: 1, transform: 'none' },
];
// A copy of a stone's photo, fixed over the page, for the flight to the strand and for removals.
const ghostOf = (src, rect) => {
  const ghost = document.createElement('img');
  ghost.className = 'lae-flight';
  ghost.alt = '';
  ghost.setAttribute('aria-hidden', 'true');
  ghost.src = src;
  Object.assign(ghost.style, { left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px` });
  document.body.append(ghost);
  return ghost;
};
const onScreen = (rect) => !!rect && rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight;

// The theme declares `const Theme` in a classic script: a global name, but not a property of window.
const themeRoutes = () => (typeof Theme !== 'undefined' && Theme.routes) || {};
const DAY = 24 * 60 * 60 * 1000;

class LaeBraceletBuilder extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    try {
      this.setup();
    } catch (error) {
      console.error('[lae-bracelet]', error);
      this.showLoadFailure();
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.stuckObserver?.disconnect();
    clearTimeout(this.addedTimer);
    clearTimeout(this.urlTimer);
  }

  setup() {
    this.config = JSON.parse(this.querySelector('[data-lae-config]').textContent);
    this.variants = new Map(this.config.variants.map((v) => [String(v.id), v]));
    this.layouts = parseLayouts(this.config.layouts);
    this.layout = 'beaded';
    this.form = this.querySelector('[data-builder]');
    this.form.setAttribute('autocomplete', 'off');
    // Start from the server-rendered defaults so a browser's restored radios never disagree with state.
    this.form.reset();

    const q = (s) => this.querySelector(s);
    const qa = (s) => Array.from(this.querySelectorAll(s));
    this.els = {
      metalInputs: qa('[data-metal-input]'),
      styleInputs: qa('[data-style-input]'),
      styleOptions: qa('[data-style-option]'),
      styleNotice: q('[data-style-notice]'),
      styleGrid: q('.lae-styles'),
      monthChips: qa('[data-month-chip]'),
      monthToggles: qa('[data-month-toggle]'),
      monthPicks: qa('.lae-month-pick'),
      strand: q('[data-strand]'),
      sentinel: q('[data-strand-sentinel]'),
      beads: q('[data-beads]'),
      count: q('[data-count]'),
      hint: q('[data-strand-hint]'),
      tools: q('[data-tools]'),
      toolsLabel: q('[data-tools-label]'),
      moveEarlier: q('[data-move="-1"]'),
      moveLater: q('[data-move="1"]'),
      removeBead: q('[data-remove-bead]'),
      tiles: qa('[data-stone]'),
      stoneLists: qa('.lae-stones'),
      tabs: qa('[data-tab]'),
      panels: qa('[data-panel]'),
      sizeInputs: qa('[data-size-input]'),
      custom: q('[data-custom]'),
      customInput: q('[data-custom-input]'),
      note: q('[data-note]'),
      noteCount: q('[data-note-count]'),
      sum: Object.fromEntries(qa('[data-sum]').map((el) => [el.dataset.sum, el])),
      sumNoteRow: q('[data-sum-note-row]'),
      sumBeads: q('[data-sum-beads]'),
      sumStatus: q('[data-sum-status]'),
      prices: qa('[data-price], [data-price-total], [data-sticky-price]'),
      priceFor: q('[data-price-for]'),
      add: q('[data-add-to-cart]'),
      addLabel: q('[data-add-label]'),
      formError: q('[data-form-error]'),
      done: q('[data-done]'),
      doneText: q('[data-done-text]'),
      announce: q('[data-announce]'),
      sticky: q('[data-sticky]'),
      stickyBtn: q('[data-sticky-btn]'),
      stickyProgress: q('[data-sticky-progress]'),
      start: q('[data-start]'),
      variantImage: q('[data-variant-image]'),
      variantThumb: q('[data-variant-thumb]'),
      track: q('[data-gallery-track]'),
      thumbs: qa('[data-thumb]'),
      errors: Object.fromEntries(qa('[data-error]').map((el) => [el.dataset.error, el])),
      steps: Object.fromEntries(qa('[data-step]').map((el) => [el.dataset.step, el])),
    };

    // Cascade order for the style cards that appear after a metal switch (one run per metal).
    for (const metal of new Set(this.els.styleOptions.map((o) => o.dataset.metal))) {
      this.els.styleOptions.filter((o) => o.dataset.metal === metal).forEach((o, i) => o.style.setProperty('--i', String(i)));
    }

    this.stoneInfo = new Map();
    for (const tile of this.els.tiles) {
      if (this.stoneInfo.has(tile.dataset.stone)) continue;
      this.stoneInfo.set(tile.dataset.stone, {
        name: tile.dataset.name,
        img: tile.querySelector('img')?.getAttribute('src') || '',
        month: tile.querySelector('.lae-stone__month')?.textContent.trim() || '',
      });
    }

    const checkedStyle = this.els.styleInputs.find((i) => i.checked);
    this.state = {
      variantId: checkedStyle ? checkedStyle.value : null,
      metal: this.els.metalInputs.find((i) => i.checked)?.value || 'silver',
      stones: [],
      selected: -1,
      size: null,
      custom: '',
      note: '',
      pending: false,
      showErrors: false,
      // Stones come first, so the action bar shows the style before the size unless the ad already chose
      // it (a link with a real variant of this product; a broken one falls back to the default style).
      styleConfirmed: this.variants.has(new URLSearchParams(location.search).get('variant') || ''),
    };
    this.tracked = { started: false, completed: false };
    // Counts basket changes (any cart or discount update), so an older basket copy is never shown over a newer one.
    this.basketGen = 0;
    // Stones on their way to the strand (index -> flight): hidden in the strand until they land.
    this.incoming = new Map();
    this.snapWanted = true;
    this.restoreDesign();

    this.bind();
    this.render();
    this.setupGallery();
    this.setupSticky();
    this.setupStrandShadow();
    this.setupStyleSeen();
    this.guardDrawerScroll();
    // Back from checkout can reuse a stored copy of this page, whose basket count and drawer are out of date.
    if (performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward') this.refreshBasket();
    this.els.add.disabled = false;
  }

  // Re-render the theme's basket (header count and drawer) from the live basket. Not via a cart event:
  // in this theme any cart update event opens the drawer. Fetched directly rather than through the theme's
  // shared section renderer, which keeps a failed request and would then refuse later basket refreshes.
  async refreshBasket() {
    const ids = new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (el) => el.dataset.sectionId));
    if (!ids.size) return;
    const gen = this.basketGen;
    try {
      const { morphSection, normalizeSectionId } = await import('@theme/section-renderer');
      for (const id of ids) {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', normalizeSectionId(id));
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) return;
        const html = await response.text();
        // The basket changed while this was loading (an add, the drawer): the newer copy is already showing.
        if (gen !== this.basketGen) return;
        await morphSection(id, html);
      }
    } catch (_error) {
      /* the count corrects itself on the next basket change */
    }
  }

  /*
   * The basket as it stood before an add, so that an unclear result (a dropped connection, a server error)
   * can be checked against it: the same design may already be in the basket from earlier. Read once the
   * design is ready and again after every basket change; null while unknown.
   */
  snapshotBasket() {
    const gen = (this.snapGen = (this.snapGen || 0) + 1);
    this.basketSnap = null;
    this.snapWanted = false;
    this.snapPromise = this.readBasket(8000).then(
      (cart) => {
        if (gen !== this.snapGen) return null;
        this.basketSnap = cart;
        return cart;
      },
      () => null
    );
  }

  basketChanged() {
    this.basketGen++;
    this.snapGen = (this.snapGen || 0) + 1;
    this.basketSnap = null;
    this.snapPromise = null;
    if (this.firstProblem()) this.snapWanted = true;
    else this.snapshotBasket();
  }

  readBasket(ms) {
    return fetch(`${themeRoutes().cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout ? AbortSignal.timeout(ms) : undefined,
    }).then((r) => r.json());
  }

  // How many of this exact design the basket holds, over every line (Shopify splits lines for the 4 for 3).
  // Every key this page sends must match, and one the design doesn't have must be missing on the line too,
  // so a copy with a note never counts as the same bracelet without one.
  basketQty(cart, v, props) {
    const keys = [this.config.noteKey, 'Size', 'Gemstones', 'Stone order'];
    return (cart.items || [])
      .filter((item) => String(item.variant_id) === String(v.id) && keys.every((k) => (item.properties || {})[k] === props[k]))
      .reduce((n, item) => n + item.quantity, 0);
  }

  /*
   * Store-wide Dwell issue (also on the live site): Escape or Android's back gesture closes the cart
   * drawer's native <dialog> via its cancel event, which skips the theme's closeDialog(), so the body
   * stays position: fixed and the page cannot scroll. On this view only, route cancel through the
   * drawer's own close(), and release the lock if the dialog still closed natively.
   */
  guardDrawerScroll() {
    const drawer = document.querySelector('cart-drawer-component');
    const dialog = drawer?.querySelector('dialog');
    if (!dialog) return;
    const { signal } = this.abort;
    dialog.addEventListener(
      'cancel',
      (event) => {
        // A non-cancelable cancel (e.g. repeated Android back) closes natively; the close listener handles it.
        if (!event.cancelable || typeof drawer.close !== 'function') return;
        event.preventDefault();
        drawer.close();
      },
      { signal }
    );
    dialog.addEventListener(
      'close',
      () => {
        const { style } = document.body;
        if (style.position !== 'fixed' || document.querySelector('dialog[open]')) return;
        const top = parseInt(style.top || '0', 10) || 0;
        style.position = '';
        style.top = '';
        style.width = '';
        window.scrollTo({ top: -top, behavior: 'instant' });
      },
      { signal }
    );
  }

  showLoadFailure() {
    const add = this.querySelector('[data-add-to-cart]');
    if (add) add.disabled = true;
    const error = this.querySelector('[data-form-error]');
    if (error) {
      error.textContent =
        'The bracelet designer did not load. Refresh the page, or email lucy@laejewellery.com with your design.';
      error.hidden = false;
    }
  }

  /* Events */

  bind() {
    const { signal } = this.abort;
    const on = (el, type, fn, opts = {}) => el?.addEventListener(type, fn, { signal, ...opts });

    const confirmStyle = () => {
      this.state.styleConfirmed = true;
    };
    for (const input of this.els.metalInputs) {
      on(input, 'change', () => {
        confirmStyle();
        this.changeMetal(input.value);
      });
    }
    for (const input of this.els.styleInputs) {
      on(input, 'change', () => {
        confirmStyle();
        this.changeStyle(input.value);
      });
    }

    on(this.els.steps.stones, 'click', (event) => {
      const toggle = event.target.closest('[data-month-toggle]');
      const add = event.target.closest('[data-add-stone]');
      const remove = event.target.closest('[data-remove-stone]');
      const bead = event.target.closest('.lae-bead');
      if (toggle) this.toggleMonth(toggle);
      else if (add) {
        this.addStone(add.dataset.addStone, this.flightFrom(add));
        // A month with two birthstones: once one is picked, fold the choice away again.
        const panel = add.closest('[data-month-panel]');
        if (panel) this.closeMonth(panel, true);
      } else if (remove) this.removeStone(remove.dataset.removeStone, remove);
      else if (bead) this.selectBead(Number(bead.dataset.index));
    });
    on(this.els.steps.stones, 'keydown', (event) => {
      const panel = event.key === 'Escape' ? event.target.closest('[data-month-panel]') : null;
      if (panel) this.closeMonth(panel, true);
    });
    on(this.els.strand, 'keydown', (event) => {
      if (event.key !== 'Escape' || this.state.selected < 0) return;
      const index = this.state.selected;
      this.selectBead(index);
      this.els.beads.querySelector(`[data-index="${index}"]`)?.focus();
    });
    on(this.els.moveEarlier, 'click', () => this.moveBead(-1));
    on(this.els.moveLater, 'click', () => this.moveBead(1));
    on(this.els.removeBead, 'click', () => this.removeSelectedBead());

    for (const tab of this.els.tabs) {
      on(tab, 'click', () => this.selectTab(tab));
      on(tab, 'keydown', (event) => this.tabKeys(event));
    }

    for (const input of this.els.sizeInputs) on(input, 'change', () => this.changeSize(input.value));
    on(this.els.customInput, 'input', () => {
      this.state.custom = this.els.customInput.value;
      this.touch();
      this.render();
    });
    // Enter in the size box would submit the whole form before the note is written: go to the note instead.
    on(this.els.customInput, 'keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.els.note.focus();
    });
    // Remembered so focus moved into the basket drawer shows a ring for keyboard users only (Safari draws
    // one after a tap otherwise).
    on(this, 'keydown', () => {
      this.keyboardUsed = true;
    });
    on(this, 'pointerdown', () => {
      this.keyboardUsed = false;
    });
    // iOS Safari only shows :active press styles when a touch listener is present.
    on(this, 'touchstart', () => {}, { passive: true });
    on(this.els.note, 'input', () => {
      this.state.note = this.els.note.value;
      this.touch();
      this.render();
      this.noteAfterAdd();
    });
    // The drawer, a discount or this page's own add: the basket snapshot and any basket refresh are out of date.
    on(document, 'cart:update', () => this.basketChanged());
    on(document, 'discount:update', () => this.basketChanged());

    on(this, 'click', (event) => {
      const edit = event.target.closest('[data-edit]');
      if (edit) this.goToStep(edit.dataset.edit);
      if (event.target.closest('[data-new-design]')) this.newDesign();
      // After an add, "View basket" opens the drawer (with this page's clearer checkout button) and keeps the
      // design on screen. The error messages' basket links stay real page loads: Shopify's bot check needs one.
      const basket = event.target.closest('[data-open-basket]');
      const drawer = document.querySelector('cart-drawer-component');
      if (basket && typeof drawer?.open === 'function') {
        event.preventDefault();
        drawer.open();
        this.settleDrawerFocus();
      }
    });
    on(this.els.start, 'click', (event) => {
      event.preventDefault();
      this.goToStep('2');
    });

    on(this.form, 'submit', (event) => {
      event.preventDefault();
      this.submit();
    });
    on(this.els.stickyBtn, 'click', () => {
      const step = this.nextStep();
      if (!step) {
        this.submit();
        return;
      }
      if (step === '1') this.state.styleConfirmed = true;
      // A size is chosen but can't be used yet ("Other size" empty or invalid): say why, as the main button does.
      if (step === '3' && this.state.size) this.state.showErrors = true;
      this.render();
      this.goToStep(step);
    });

    on(window, 'pageshow', (event) => {
      if (!event.persisted) return;
      this.setPending(false);
      // Back from checkout with the page kept in memory: the basket may have changed meanwhile.
      this.basketChanged();
      this.refreshBasket();
    });
    // Back to a tab left in the background (another tab, another app): the basket may have changed there, and
    // the drawer's Check out sends its quantities line by line, so show the real basket again.
    on(document, 'visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.hiddenAt = Date.now();
        return;
      }
      if (this.hiddenAt && Date.now() - this.hiddenAt > 30000) {
        this.basketChanged();
        this.refreshBasket();
      }
      this.hiddenAt = 0;
    });
  }

  touch() {
    if (this.tracked.started) return;
    this.tracked.started = true;
    this.track('lae_bracelet_builder_started', {});
  }

  /* Bracelet */

  variant() {
    return this.state.variantId ? this.variants.get(String(this.state.variantId)) || null : null;
  }

  changeMetal(metal) {
    if (metal === this.state.metal) return;
    this.touch();
    const current = this.variant();
    // Each metal remembers its last style, so switching there and back restores it.
    this.lastByMetal ??= {};
    if (current) this.lastByMetal[current.metal] = String(current.id);
    this.state.metal = metal;
    const metalName = this.config.metalNames[metal].toLowerCase();
    const twin = current ? this.twinOf(current, metal) : null;
    const remembered = this.variants.get(this.lastByMetal[metal] || '');
    let match = null;
    if (twin) match = twin.available ? twin : null;
    else if (remembered && remembered.available) match = remembered;

    if (match) {
      this.setVariant(match.id);
      // Without a twin the remembered style is used, so say why the chosen design changed.
      this.els.styleNotice.textContent = twin
        ? match.label === current.label
          ? `Showing ${match.label} in ${metalName}. Each photo shows that style.`
          : `The same design in ${metalName} is ${match.label}. Each photo shows that style.`
        : current
          ? `${current.label} isn't made in ${metalName}. Back to ${match.label}, the ${metalName} style you chose before. Each photo shows that style.`
          : `Back to ${match.label} in ${metalName}. Each photo shows that style.`;
    } else {
      this.state.variantId = null;
      for (const input of this.els.styleInputs) input.checked = false;
      this.writeVariantParam(null);
      this.els.styleNotice.textContent = !current
        ? 'Each photo shows that style.'
        : twin
          ? `${twin.label} in ${metalName} is unavailable right now. Choose another ${metalName} style below.`
          : `${current.label} isn't made in ${metalName}. Choose one of the ${metalName} styles below.`;
    }
    this.render();
    this.announce(this.els.styleNotice.textContent);
  }

  // Style letters run on separately in each metal (gold has no heart styles, so silver D, E and F are
  // gold C, D and E). The same design is the same name without "(style X)" and the same stone strip
  // layout; the letter only breaks a tie (Beaded A and B).
  twinOf(v, metal) {
    const design = (x) => `${x.label.replace(/\s*\(style[^)]*\)/i, '').trim()}|${this.layoutFor(x)}`;
    const twins = this.config.variants.filter((x) => x.metal === metal && design(x) === design(v));
    return twins.find((x) => x.label === v.label) || (twins.length === 1 ? twins[0] : null);
  }

  // Keeps every other query parameter (view, attribution) and only sets or removes ?variant. Meta's pixel
  // and GA4 count each URL change as a page view, so the URL is updated once a choice has settled.
  writeVariantParam(id) {
    clearTimeout(this.urlTimer);
    this.urlTimer = setTimeout(() => {
      const url = new URL(window.location.href);
      if ((url.searchParams.get('variant') || null) === id) return;
      if (id) url.searchParams.set('variant', id);
      else url.searchParams.delete('variant');
      window.history.replaceState(window.history.state, '', url);
      // The saved design notes the address it belongs to; keep that in step with the page's own rewrite.
      if (this.savedJson) this.storeDesign(this.savedJson);
    }, 1200);
  }

  changeStyle(id) {
    this.touch();
    this.setVariant(id);
    this.els.styleNotice.textContent = 'Each photo shows that style.';
    this.render();
    const v = this.variant();
    if (v) this.announce(`${v.label}, ${v.priceText}.`);
  }

  setVariant(id, { writeUrl = true } = {}) {
    const v = this.variants.get(String(id));
    if (!v) return;
    this.state.variantId = String(v.id);
    for (const input of this.els.styleInputs) input.checked = input.value === String(v.id);
    if (writeUrl) this.writeVariantParam(String(v.id));

    const img = this.els.variantImage;
    if (img && v.src && img.dataset.variantId !== String(v.id)) {
      if (this.state && img.dataset.variantId) {
        img.addEventListener('load', () => animate(img, [{ opacity: 0.35 }, { opacity: 1 }], { duration: 360, easing: EASE }), { once: true });
      }
      img.srcset = v.srcset;
      img.src = v.src;
      img.dataset.variantId = String(v.id);
    }
    if (img) img.alt = `${v.label} bracelet in ${this.config.metalNames[v.metal].toLowerCase()}`;
    if (this.els.variantThumb && v.thumb) {
      this.els.variantThumb.removeAttribute('srcset');
      this.els.variantThumb.src = v.thumb;
    }
    this.showSlide(0);
  }

  /* Stones */

  addStone(handle, from = null) {
    this.touch();
    const { max } = this.config;
    const info = this.stoneInfo.get(handle);
    if (!info) return;
    if (this.state.stones.length >= max) {
      this.nudgeLimit();
      return;
    }
    this.planStrand('add');
    this.state.stones.push(handle);
    this.state.selected = -1;
    this.justAdded = this.state.stones.length - 1;
    if (from) {
      this.incoming.set(this.justAdded, from);
      this.flight = this.justAdded;
    }
    this.render();
    this.announce(
      this.state.stones.length >= max
        ? `${info.name} added. That's ${max} stones, the most this bracelet holds.`
        : `${info.name} added. ${this.countText()}, up to ${max}.`
    );
  }

  // The limit message shows in the strand (in place of any open stone controls); draw the eye to it.
  nudgeLimit() {
    const { max } = this.config;
    if (this.state.selected >= 0) {
      this.state.selected = -1;
      this.render();
    }
    const { hint } = this.els;
    hint.classList.remove('is-nudge');
    void hint.offsetWidth;
    hint.classList.add('is-nudge');
    this.announce(`That's ${max} stones, the most one bracelet holds. To swap one, remove a stone first. For more people, add this bracelet, then design a second one.`);
  }

  toggleMonth(toggle) {
    const panel = document.getElementById(toggle.dataset.monthToggle);
    if (!panel) return;
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    // A full bracelet: say so, as the one-stone months do, rather than open a choice that can't be used.
    if (open && this.state.stones.length >= this.config.max) {
      this.nudgeLimit();
      return;
    }
    for (const other of this.els.monthToggles) {
      if (other !== toggle) this.closeMonth(document.getElementById(other.dataset.monthToggle));
    }
    toggle.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    if (open) panel.querySelector('[data-add-stone]')?.focus({ preventScroll: true });
  }

  closeMonth(panel, focusToggle = false) {
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    const toggle = this.querySelector(`[data-month-toggle="${panel.id}"]`);
    toggle?.setAttribute('aria-expanded', 'false');
    if (focusToggle) toggle?.focus({ preventScroll: true });
  }

  removeStone(handle, trigger) {
    const index = this.state.stones.lastIndexOf(handle);
    if (index < 0) return;
    this.planStrand('remove', index);
    this.state.stones.splice(index, 1);
    this.state.selected = -1;
    this.render();
    this.announce(`One ${this.stoneInfo.get(handle).name} removed. ${this.countText()}.`);
    // The minus button hides when the last bead of that stone goes; keep focus on its tile.
    if (trigger && trigger.hidden) trigger.closest('[data-stone]')?.querySelector('[data-add-stone]')?.focus();
  }

  selectBead(index) {
    this.state.selected = this.state.selected === index ? -1 : index;
    this.render();
    if (this.state.selected < 0) return;
    const name = this.stoneInfo.get(this.state.stones[index]).name;
    this.announce(
      this.config.orderedUnits
        ? `${name} chosen. Move it or remove it with the buttons under your stones.`
        : `${name} chosen. Remove it with the button under your stones.`
    );
  }

  moveBead(direction) {
    const { stones } = this.state;
    const from = this.state.selected;
    const to = from + direction;
    if (from < 0 || to < 0 || to >= stones.length) return;
    this.planStrand('move', from, to);
    [stones[from], stones[to]] = [stones[to], stones[from]];
    this.state.selected = to;
    this.render();
    this.announce(`${this.stoneInfo.get(stones[to]).name} moved to position ${to + 1} of ${stones.length}.`);
    const stay = direction < 0 ? this.els.moveEarlier : this.els.moveLater;
    (stay.disabled ? (direction < 0 ? this.els.moveLater : this.els.moveEarlier) : stay).focus();
  }

  removeSelectedBead() {
    const index = this.state.selected;
    if (index < 0) return;
    this.planStrand('remove', index);
    const [handle] = this.state.stones.splice(index, 1);
    this.state.selected = -1;
    this.render();
    this.announce(`${this.stoneInfo.get(handle).name} removed. ${this.countText()}.`);
    const beads = this.els.beads.querySelectorAll('.lae-bead');
    (beads[Math.min(index, beads.length - 1)] || this.els.steps.stones).focus();
  }

  selectTab(tab) {
    for (const t of this.els.tabs) {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    }
    for (const panel of this.els.panels) panel.hidden = panel.dataset.panel !== tab.dataset.tab;
  }

  tabKeys(event) {
    const i = this.els.tabs.indexOf(event.currentTarget);
    const last = this.els.tabs.length - 1;
    const next = { ArrowRight: i === last ? 0 : i + 1, ArrowLeft: i === 0 ? last : i - 1, Home: 0, End: last }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.selectTab(this.els.tabs[next]);
    this.els.tabs[next].focus();
  }

  // Worded so the maximum reads as a limit, not a target: one stone is a complete bracelet.
  countText() {
    const n = this.state.stones.length;
    return `${n} ${n === 1 ? 'stone' : 'stones'} chosen`;
  }

  countLabel() {
    const n = this.state.stones.length;
    const { max } = this.config;
    if (!n) return `1 to ${max} stones`;
    return n >= max ? `${n} chosen, the maximum` : `${n} chosen, up to ${max}`;
  }

  /* Fit */

  changeSize(value) {
    this.touch();
    this.state.size = value;
    this.render();
    if (value === 'custom') this.els.customInput.focus();
  }

  customSize() {
    const raw = this.state.custom.trim().replace(',', '.').replace(/\s*cm$/i, '');
    if (!raw) return { ok: false, message: 'Enter your bracelet size in centimetres, for example 17.5.' };
    if (!/^\d{1,2}(\.\d)?$/.test(raw)) {
      return { ok: false, message: 'Use numbers only, with up to one decimal place, for example 17.5.' };
    }
    const value = Number(raw);
    const { customMin, customMax } = this.config;
    if (value < customMin || value > customMax) {
      return { ok: false, message: `Enter a size between ${customMin} and ${customMax} cm.` };
    }
    return { ok: true, value: String(value) };
  }

  sizeValue() {
    if (!this.state.size) return null;
    if (this.state.size !== 'custom') return this.state.size;
    const custom = this.customSize();
    return custom.ok ? `Custom: ${custom.value}cm` : null;
  }

  /* Validation */

  problems() {
    const problems = {};
    const v = this.variant();
    if (!v) problems.bracelet = 'Choose a style for your bracelet.';
    else if (!v.available) problems.bracelet = 'This style is unavailable right now. Choose another style.';
    if (this.state.stones.length < this.config.min) problems.stones = 'Choose at least 1 stone.';
    if (!this.state.size) problems.fit = 'Choose a size.';
    else if (this.state.size === 'custom' && !this.customSize().ok) problems.fit = this.customSize().message;
    return problems;
  }

  firstProblem() {
    const p = this.problems();
    return p.bracelet || p.stones || p.fit || null;
  }

  firstProblemStep() {
    const p = this.problems();
    if (p.bracelet) return '1';
    if (p.stones) return '2';
    return '3';
  }

  // The phone action bar's next stop. After the stones it shows the style, unless the ad chose it
  // (a variant in the link) or the customer has already seen or changed it; null means ready to add.
  nextStep() {
    const p = this.problems();
    if (p.bracelet) return '1';
    if (p.stones) return '2';
    if (p.fit && !this.state.styleConfirmed) return '1';
    if (p.fit) return '3';
    return null;
  }

  goToStep(step) {
    const map = { 1: 'bracelet', 2: 'stones', 3: 'fit', note: 'fit' };
    const target = this.els.steps[map[step]];
    if (!target) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    let focusEl = target;
    if (step === 'note') focusEl = this.els.note;
    else if (step === '1') focusEl = this.els.styleInputs.find((i) => i.checked) || this.els.metalInputs.find((i) => i.checked);
    else if (step === '3') {
      focusEl =
        this.state.size === 'custom' && !this.customSize().ok
          ? this.els.customInput
          : this.els.sizeInputs.find((i) => i.checked) || this.els.sizeInputs[0];
    }
    // keepFocusVisible must not react to this focus: its instant scroll would cancel the smooth one above.
    this.autoScrolling = true;
    (focusEl || target).focus({ preventScroll: true });
    this.autoScrolling = false;
    // Once the scroll has arrived, the step's number gives one soft ring: "you are here".
    const num = target.querySelector('.lae-step__num');
    if (num && canAnimate()) {
      setTimeout(() => {
        animate(num, [{ boxShadow: '0 0 0 0 rgb(140 69 50 / 0.45)' }, { boxShadow: '0 0 0 12px rgb(140 69 50 / 0)' }], { duration: 900, easing: EASE });
      }, 450);
    }
  }

  /* Rendering: everything visible is derived from state here. */

  render() {
    const { state, config, els } = this;
    const v = this.variant();
    const n = state.stones.length;
    const full = n >= config.max;

    for (const opt of els.styleOptions) opt.hidden = opt.dataset.metal !== state.metal;
    for (const input of els.metalInputs) input.checked = input.value === state.metal;
    els.strand.dataset.metal = v ? v.metal : state.metal;

    // Prices always come from the selected variant; with no style chosen show the honest "from" price.
    const metalVariants = config.variants.filter((x) => x.metal === state.metal);
    const cheapest = metalVariants.reduce((a, b) => (b.price < a.price ? b : a), metalVariants[0]);
    const priceText = v ? v.priceText : `from ${cheapest.priceText}`;
    for (const el of els.prices) el.textContent = priceText;
    if (this.lastPrice !== undefined && this.lastPrice !== priceText) {
      for (const el of els.prices) animate(el, FADE_UP, { duration: 320, easing: EASE });
    }
    this.lastPrice = priceText;
    const braceletText = v ? `${config.metalNames[v.metal]}, ${keepStyle(v.label)}` : 'Not chosen yet';
    els.priceFor.textContent = v ? braceletText : `${config.metalNames[state.metal]}, choose a style`;

    // Tiles
    const counts = new Map();
    for (const h of state.stones) counts.set(h, (counts.get(h) || 0) + 1);
    for (const tile of els.tiles) {
      const c = counts.get(tile.dataset.stone) || 0;
      if (tile.classList.contains('is-chosen') !== c > 0) tile.classList.toggle('is-chosen', c > 0);
      tile.laeParts ??= {
        badge: tile.querySelector('[data-count-badge]'),
        remove: tile.querySelector('[data-remove-stone]'),
        add: tile.querySelector('[data-add-stone]'),
        state: tile.querySelector('[data-stone-state]'),
      };
      const { badge, remove, add, state: stateEl } = tile.laeParts;
      const was = Number(badge.textContent) || 0;
      setHidden(badge, c === 0);
      setText(badge, String(c));
      // The badge pops in (CSS) when it first shows; a higher count bumps it.
      if (was > 0 && c > was) animate(badge, BUMP, { duration: 280, easing: SPRING });
      setHidden(remove, c === 0);
      setAttr(add, 'aria-disabled', String(full));
      const month = this.stoneInfo.get(tile.dataset.stone).month;
      setText(stateEl, [month ? `${month} birthstone.` : '', c > 0 ? `${c} in your bracelet.` : ''].join(' ').trim());
    }
    for (const list of els.stoneLists) list.classList.toggle('is-full', full);
    for (const chip of els.monthChips) {
      chip.laeHandles ??= chip.dataset.monthChip.split(',');
      chip.laeCount ??= chip.querySelector('[data-month-count]');
      const c = chip.laeHandles.reduce((sum, h) => sum + (counts.get(h) || 0), 0);
      if (chip.classList.contains('is-chosen') !== c > 0) chip.classList.toggle('is-chosen', c > 0);
      const was = Number(chip.laeCount.textContent) || 0;
      setHidden(chip.laeCount, c === 0);
      setText(chip.laeCount, String(c));
      if (was > 0 && c > was) animate(chip.laeCount, BUMP, { duration: 280, easing: SPRING });
      setAttr(chip, 'aria-disabled', String(full));
    }
    for (const pick of els.monthPicks) setAttr(pick, 'aria-disabled', String(full));

    this.renderBeads();
    els.count.textContent = this.countLabel();

    // Fit
    for (const input of els.sizeInputs) input.checked = input.value === state.size;
    els.custom.hidden = state.size !== 'custom';
    const noteLength = state.note.length;
    els.noteCount.textContent = `${noteLength} of ${config.noteMax} characters`;

    // Summary
    els.sum.bracelet.textContent = braceletText;
    els.sum.stones.textContent = n ? this.groupedNames(' ×').join(', ') : 'Not chosen yet';
    els.sumBeads.replaceChildren(
      ...state.stones.map((handle) => {
        const bead = document.createElement('span');
        bead.className = 'lae-summary__bead';
        const img = document.createElement('img');
        img.src = this.stoneInfo.get(handle).img;
        img.alt = '';
        img.width = 22;
        img.height = 22;
        bead.append(img);
        return bead;
      })
    );
    const size = this.sizeValue();
    els.sum.size.textContent = size
      ? size.replace(/^Custom: (.*)cm$/, 'Custom, $1 cm')
      : state.size === 'custom'
        ? 'Custom size to enter'
        : 'Not chosen yet';
    const note = state.note.trim();
    els.sumNoteRow.hidden = !note;
    els.sum.note.textContent = note;

    // Errors only appear after a purchase attempt, then update live as the customer fixes them.
    const problems = this.problems();
    for (const [key, el] of Object.entries(els.errors)) {
      const message = state.showErrors ? problems[key] || '' : '';
      el.textContent = message;
      el.hidden = !message;
    }
    const customProblem = state.showErrors && state.size === 'custom' && !this.customSize().ok;
    els.customInput.setAttribute('aria-invalid', String(customProblem));

    // Says beside the button what it still needs, so pressing it early is never a surprise.
    const todo = [];
    if (problems.bracelet) todo.push('your style');
    if (problems.stones) todo.push('your stones');
    if (problems.fit) todo.push(state.size === 'custom' ? 'a size in centimetres' : 'your size');
    els.sumStatus.textContent = todo.length ? `Still to choose: ${listText(todo)}.` : '';
    els.sumStatus.hidden = !todo.length;

    if (!this.firstProblem() && !this.tracked.completed) {
      this.tracked.completed = true;
      this.track('lae_bracelet_builder_completed', { metal: v.metal, stone_count: n });
    }

    this.renderSticky();
    this.saveDesign();
    if (this.snapWanted && !this.firstProblem()) this.snapshotBasket();
  }

  layoutFor(v) {
    if (!v) return this.layout;
    return this.layouts.get(String(v.option || '').toLowerCase()) || guessLayout(v.label);
  }

  renderBeads() {
    const { state, els, config } = this;
    const n = state.stones.length;
    // Keep the last layout while no style is chosen (for example right after a metal change).
    const layout = this.layoutFor(this.variant());
    this.layout = layout;
    els.strand.dataset.layout = layout;
    const between = layout === 'bars-between';
    const either = layout.startsWith('bars-either-side');

    const deco = (kind) => {
      const li = document.createElement('li');
      li.className = `lae-strand__${kind}`;
      li.setAttribute('aria-hidden', 'true');
      return li;
    };
    const stoneItems = state.stones.map((handle, i) => {
      const info = this.stoneInfo.get(handle);
      const li = document.createElement('li');
      li.className = 'lae-strand__stone';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lae-bead' + (this.incoming.has(i) ? ' is-incoming' : i === this.justAdded ? ' lae-bead--new' : '');
      button.dataset.index = String(i);
      button.setAttribute('aria-pressed', String(i === state.selected));
      const img = document.createElement('img');
      img.src = info.img;
      img.alt = '';
      img.width = 48;
      img.height = 48;
      const label = document.createElement('span');
      label.className = 'lae-sr';
      label.textContent = `Stone ${i + 1} of ${n}: ${info.name}`;
      button.append(img, label);
      li.append(button);
      return li;
    });

    // Grouped styles: [bar] bead, stones, +, bead [bar].
    // Spaced style: the style's own pattern of positions with a bar between each, shown even before any
    // stone is chosen; positions fill from the left, and a fifth stone onwards adds a bar and a position.
    const items = [];
    const next = n < config.max ? deco('next') : null;
    next?.classList.toggle('is-optional', n > 0);
    const positions = between ? Math.min(config.max, Math.max(BARS_BETWEEN_POSITIONS, n + 1)) : 0;
    // Beyond five positions the bars become short plain tubes so every stone still fits on a phone.
    els.strand.classList.toggle('is-crowded', positions > 5);
    if (between) {
      for (let i = 0; i < positions; i++) {
        if (i > 0) items.push(deco('bar'));
        items.push(i < n ? stoneItems[i] : i === n ? next : deco('slot'));
      }
    } else {
      if (either) items.push(deco('end'), deco('bar'));
      items.push(deco('end'), ...stoneItems);
      if (next) items.push(next);
      items.push(deco('end'));
      if (either) items.push(deco('bar'), deco('end'));
    }

    // Heart styles: the charm hangs from the centre stone, or with an even number from the stone just
    // right of the middle (as Lucy makes them).
    // It swings a little when it comes to hang from a different stone.
    const heartAt = layout.endsWith('heart') ? Math.floor(n / 2) : -1;
    const heartItem = heartAt < 0 ? null : n ? stoneItems[heartAt] : next;
    heartItem?.classList.add('has-heart');
    const heartKey = heartItem ? `${heartAt}|${state.stones[heartAt] || ''}` : '';
    if (heartItem && this.heartKey !== undefined && heartKey !== this.heartKey) heartItem.classList.add('is-swing');
    this.heartKey = heartKey;

    const focusedIndex = els.beads.contains(document.activeElement) ? document.activeElement.dataset.index : null;
    els.beads.replaceChildren(...items);
    if (focusedIndex !== null) els.beads.querySelector(`[data-index="${focusedIndex}"]`)?.focus();
    this.justAdded = -1;
    // Next frame, before it is painted: glide the strand from where it was, and fly in a new stone.
    const plan = this.strandPlan;
    const flight = this.flight;
    this.strandPlan = null;
    this.flight = undefined;
    if (plan || flight !== undefined) {
      requestAnimationFrame(() => {
        if (plan) this.playStrand(plan);
        if (flight !== undefined) this.fly(flight);
      });
    }

    // One short line under the stones (it sits in the sticky strand on phones).
    const canMove = config.orderedUnits;
    const full = n >= config.max;
    const action = canMove ? 'move or remove it' : 'remove it';
    els.hint.classList.toggle('is-limit', full);
    els.hint.textContent = !n
      ? 'Click a month or stone below to add it.'
      : full
        ? `That's ${config.max} stones, the most this bracelet holds. Click one to ${action}.`
        : `More are optional. Click one to ${action}.`;

    // The chosen stone's controls take the hint's place, so the strand keeps the same height.
    const sel = state.selected;
    const open = sel >= 0 && sel < n;
    els.tools.hidden = !open;
    els.hint.hidden = open;
    els.moveEarlier.hidden = !canMove;
    els.moveLater.hidden = !canMove;
    if (open) {
      const name = this.stoneInfo.get(state.stones[sel]).name;
      els.toolsLabel.textContent = name;
      els.moveEarlier.setAttribute('aria-label', `Move ${name} left`);
      els.moveLater.setAttribute('aria-label', `Move ${name} right`);
      els.removeBead.setAttribute('aria-label', `Remove ${name}`);
      els.moveEarlier.disabled = sel === 0;
      els.moveLater.disabled = sel === n - 1;
    }
  }

  /* Strand motion. The strand is rebuilt on every change, so movement is drawn from measurements: where
     each item was before the change, and where it is after (the "FLIP" technique). */

  // Where a tapped tile or month's stone photo is, for its flight to the strand (null: no flight).
  flightFrom(button) {
    if (!canAnimate()) return null;
    const img = button.querySelector('img');
    const rect = img?.getBoundingClientRect();
    return img && onScreen(rect) ? { src: img.currentSrc || img.src, rect } : null;
  }

  // Before the stones change: where each strand item is now, and which old stone each new one was.
  planStrand(kind, a = -1, b = -1) {
    if (!canAnimate()) return;
    // Indices shift on a removal or a move, so stones still in flight just appear.
    if (kind !== 'add') this.incoming.clear();
    const rects = new Map();
    const counts = {};
    for (const li of this.els.beads.children) rects.set(this.strandKey(li, counts), li.getBoundingClientRect());
    const oldIndex = (i) => {
      if (kind === 'remove') return i < a ? i : i + 1;
      if (kind === 'move') return i === b ? a : i === a ? b : i;
      return i;
    };
    this.strandPlan = { rects, oldIndex };
    if (kind === 'remove') {
      const img = this.els.beads.querySelector(`[data-index="${a}"] img`);
      const rect = img?.getBoundingClientRect();
      if (onScreen(rect)) {
        const ghost = ghostOf(img.currentSrc || img.src, rect);
        const out = animate(ghost, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(10px) scale(0.5)' }], {
          duration: 240,
          easing: 'cubic-bezier(0.55, 0, 1, 0.45)',
        });
        const done = () => {
          window.removeEventListener('scroll', done);
          ghost.remove();
        };
        out ? out.finished.then(done, done) : done();
        window.addEventListener('scroll', done, { passive: true, once: true });
        setTimeout(done, 600);
      }
    }
  }

  // Stones are keyed by position (mapped through the change); bars, end beads and the "+" by their order.
  strandKey(li, counts, oldIndex) {
    const bead = li.querySelector('.lae-bead');
    if (bead) return `stone${oldIndex ? oldIndex(Number(bead.dataset.index)) : bead.dataset.index}`;
    const kind = li.className.match(/lae-strand__(\w+)/)?.[1] || 'item';
    counts[kind] = (counts[kind] || 0) + 1;
    return `${kind}${counts[kind]}`;
  }

  playStrand({ rects, oldIndex }) {
    const counts = {};
    for (const li of this.els.beads.children) {
      const was = rects.get(this.strandKey(li, counts, oldIndex));
      if (!was) continue;
      const now = li.getBoundingClientRect();
      const dx = was.left - now.left;
      const dy = was.top - now.top;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
      animate(li, [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 340, easing: EASE });
    }
  }

  // The stone's photo flies from the tile to its place on the strand; the bead appears as it lands.
  fly(index) {
    const from = this.incoming.get(index);
    if (!from) return;
    const target = this.els.beads.querySelector(`[data-index="${index}"]`);
    const land = () => {
      if (!this.incoming.delete(index)) return;
      const bead = this.els.beads.querySelector(`[data-index="${index}"]`);
      bead?.classList.remove('is-incoming');
      animate(bead, LAND, { duration: 300, easing: SPRING });
    };
    const to = target?.querySelector('img')?.getBoundingClientRect();
    if (!onScreen(to)) {
      // The strand is off screen: no flight, the bead just drops in.
      this.incoming.delete(index);
      target?.classList.remove('is-incoming');
      animate(target, BEAD_IN, { duration: 380, easing: EASE });
      return;
    }
    const ghost = ghostOf(from.src, from.rect);
    const dx = to.left - from.rect.left;
    const dy = to.top - from.rect.top;
    const scale = to.width / from.rect.width;
    const trip = animate(
      ghost,
      [
        { transform: 'translate(0, 0) scale(1)' },
        { transform: `translate(${dx * 0.55}px, ${dy * 0.55 - 28}px) scale(${((1 + scale) / 2) * 1.06})`, offset: 0.55 },
        { transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
      ],
      { duration: 460, easing: 'cubic-bezier(0.45, 0, 0.25, 1)' }
    );
    const done = () => {
      window.removeEventListener('scroll', done);
      ghost.remove();
      land();
    };
    trip ? trip.finished.then(done, done) : done();
    // The copy flies in screen coordinates, so if the page scrolls the stone just appears in its place.
    window.addEventListener('scroll', done, { passive: true, once: true });
    // Never leave a stone hidden: a background tab can pause animations.
    setTimeout(done, 900);
  }

  // A soft shadow under the strand only while it is stuck over the stone grid.
  setupStrandShadow() {
    const { sentinel, strand } = this.els;
    if (!sentinel || !('IntersectionObserver' in window)) return;
    this.stuckObserver = new IntersectionObserver(([entry]) => {
      strand.classList.toggle('is-stuck', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    this.stuckObserver.observe(sentinel);
  }

  renderSticky() {
    const { els, state } = this;
    const p = this.problems();
    // Kept short: the bar has about 30 characters on a 375 px phone.
    const stonesShort = `${state.stones.length} ${state.stones.length === 1 ? 'stone' : 'stones'}`;
    let progress = 'Ready to add to your basket';
    if (p.bracelet) progress = 'Choose a style';
    else if (p.stones) progress = 'Choose your stones';
    else if (p.fit && !state.styleConfirmed) progress = `${stonesShort}, now choose a style`;
    else if (p.fit) progress = state.size ? 'Check your size' : `${stonesShort}, now choose a size`;
    if (this.lastProgress !== undefined && this.lastProgress !== progress && !els.sticky.classList.contains('is-away')) {
      animate(els.stickyProgress, FADE_UP, { duration: 280, easing: EASE });
    }
    this.lastProgress = progress;
    els.stickyProgress.textContent = progress;
    const ready = !this.firstProblem();
    els.stickyBtn.textContent = state.pending ? 'Adding…' : ready ? 'Add to basket' : 'Continue';
    els.stickyBtn.setAttribute('aria-disabled', String(state.pending));
  }

  groupedNames(joiner) {
    const counts = new Map();
    for (const h of this.state.stones) {
      const name = this.stoneInfo.get(h).name;
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return Array.from(counts, ([name, c]) => (joiner === ' ×' ? (c > 1 ? `${name} ×${c}` : name) : `${name} (${c})`));
  }

  /* Cart */

  properties() {
    const props = {};
    const note = this.state.note.trim().replace(/[\u0000-\u001f\u007f]+/g, ' ').slice(0, this.config.noteMax);
    if (note) props[this.config.noteKey] = note;
    props.Size = this.sizeValue();
    props.Gemstones = this.groupedNames().join(', ');
    // Additive: the existing Gemstones line groups repeats, so the bead sequence is sent separately.
    if (this.config.orderedUnits && this.state.stones.length > 1) {
      props['Stone order'] = this.state.stones.map((h) => this.stoneInfo.get(h).name).join(', ');
    }
    return props;
  }

  submit() {
    if (this.state.pending) return;
    this.els.formError.hidden = true;
    const problems = this.problems();
    const keys = Object.keys(problems);
    if (keys.length) {
      this.state.showErrors = true;
      this.render();
      this.track('lae_bracelet_builder_error', { category: keys.join(',') });
      this.goToStep(this.firstProblemStep());
      this.announce(this.firstProblem());
      return;
    }
    this.addToCart();
  }

  setPending(on) {
    this.state.pending = on;
    const { add, addLabel } = this.els;
    add.setAttribute('aria-busy', String(on));
    if (on) add.classList.remove('is-added');
    add.setAttribute('aria-disabled', String(on));
    addLabel.textContent = on ? 'Adding your bracelet…' : 'Add bracelet to basket';
    this.renderSticky();
  }

  async addToCart() {
    const v = this.variant();
    const props = this.properties();
    this.adding = { json: this.designJson(), note: props[this.config.noteKey] || '' };
    this.setPending(true);
    this.els.done.hidden = true;
    // How many of this design the basket already held, for checking an unclear result (null if unknown).
    // If the basket is still being read (the design was only just finished), wait a moment for it.
    let snap = this.basketSnap;
    if (!snap && this.snapPromise) snap = await Promise.race([this.snapPromise, new Promise((resolve) => setTimeout(() => resolve(null), 1500))]);
    const before = snap ? this.basketQty(snap, v, props) : null;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (el) => el.dataset.sectionId);
    const body = { items: [{ id: Number(v.id), quantity: 1, properties: props }] };
    if (sectionIds.length) body.sections = Array.from(new Set(sectionIds)).join(',');

    const url = themeRoutes().cart_add_url || '/cart/add.js';
    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), REQUEST_TIMEOUT);
    let response;
    let data = null;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal: timeout.signal,
      });
      data = await response.json().catch(() => null);
    } catch (_error) {
      clearTimeout(timer);
      // The request may or may not have reached Shopify. Check the basket before inviting a retry.
      await this.reconcile(v, props, 0, before);
      return;
    }
    clearTimeout(timer);

    // A 2xx whose body could not be read, or a 5xx, may still have added the item: check before advising a retry.
    if ((response.ok && !data) || response.status >= 500) {
      await this.reconcile(v, props, response.status, before);
      return;
    }
    // Shopify's bot check: a fetch can't pass it, a normal page load can.
    if (response.status === 429) {
      this.track('lae_bracelet_builder_error', { category: 'cart_429' });
      this.fail("My shop needs to check your connection first. Open your basket, then come back and add your bracelet again. Your design will still be here.", true);
      return;
    }
    if (!response.ok || !data || data.status) {
      const reason = String((data && (data.description || data.message)) || '').trim();
      this.track('lae_bracelet_builder_error', { category: `cart_${response.status}` });
      this.fail(
        reason
          ? `Your bracelet wasn't added: ${/[.!?]$/.test(reason) ? reason : `${reason}.`} Your design is still here.`
          : "Your bracelet wasn't added. Please try again. Your design is still here."
      );
      return;
    }
    this.succeed(v, data.sections, null);
  }

  // An unclear add (no readable answer): look in the basket. "Landed" means more of this exact design than
  // the basket held before the add; with no earlier count, any copy could be an older one.
  async reconcile(v, props, status, before) {
    const landed = (cart) => {
      const count = this.basketQty(cart, v, props);
      return before === null ? count > 0 : count > before;
    };
    const dropped = status === 0;
    const unsure = "I couldn't confirm whether your bracelet was added. Check your basket before trying again. Your design is still here.";
    try {
      let cart = await this.readBasket(8000);
      if (!landed(cart)) {
        // An aborted request can still be processed by Shopify a moment later. Look once more.
        await new Promise((resolve) => setTimeout(resolve, 2000));
        cart = await this.readBasket(8000);
      }
      if (landed(cart) && before !== null) {
        this.track('lae_bracelet_builder_error', { category: 'ambiguous_found' });
        this.succeed(
          v,
          null,
          cart.item_count,
          dropped
            ? 'The connection dropped, but your bracelet is in your basket. Check your basket before adding it again.'
            : 'Your bracelet is in your basket.'
        );
        return;
      }
      // Show the real basket in the header, so "Check your basket" shows what is actually there.
      this.refreshBasket();
      if (landed(cart)) {
        this.track('lae_bracelet_builder_error', { category: 'ambiguous_unknown' });
        this.fail(unsure, true);
        return;
      }
      this.track('lae_bracelet_builder_error', { category: dropped ? 'network' : `cart_${status}` });
      this.fail(
        dropped
          ? "Your bracelet doesn't seem to have been added because the connection dropped. Check your basket, then try again. Your design is still here."
          : "Your bracelet wasn't added because my shop didn't respond properly. Check your basket, then try again. Your design is still here.",
        true
      );
    } catch (_error) {
      this.track('lae_bracelet_builder_error', { category: 'ambiguous_unknown' });
      this.fail(unsure, true);
    }
  }

  succeed(v, sections, itemCount, message) {
    // source 'product-form-component' tells the theme's cart icon to add itemCount to its count,
    // exactly as Dwell's own product form does. With a known total we pass it with a neutral source.
    const detail =
      itemCount === null
        ? { source: 'product-form-component', itemCount: 1, productId: String(this.config.productId), sections }
        : { source: 'lae-bracelet-campaign', itemCount, productId: String(this.config.productId) };
    this.dispatchEvent(new CartAddEvent({}, String(v.id), detail));
    // Now, and again once an asynchronous re-render of the drawer has settled.
    this.settleDrawerFocus();
    setTimeout(() => this.settleDrawerFocus(), 700);
    // On a slow connection this page can be ready before the theme's basket code: if that hasn't loaded
    // yet, nothing heard the add, so show the real basket once it has.
    if (!customElements.get('cart-items-component')) {
      customElements.whenDefined('cart-items-component').then(() => this.refreshBasket());
    }
    // The design on screen is the one just added: it stays out of storage until the customer changes it.
    this.addedJson = this.adding?.json || this.designJson();
    this.addedNote = this.adding?.note || '';
    this.forgetDesign();

    this.setPending(false);
    const text = message || 'Your bracelet is in your basket.';
    this.doneMessage = text;
    this.noteWarned = false;
    this.els.doneText.textContent = text;
    this.els.done.hidden = false;
    this.els.addLabel.textContent = 'Added to basket';
    this.els.add.classList.add('is-added');
    clearTimeout(this.addedTimer);
    this.addedTimer = setTimeout(() => {
      this.els.add.classList.remove('is-added');
      if (!this.state.pending) this.els.addLabel.textContent = 'Add bracelet to basket';
    }, 2500);
    this.announce(text);
  }

  // The drawer is modal, so content outside it (including our live region) is inert: focus belongs inside,
  // with a ring for keyboard users only. WebKit marks the dialog's own first focus as visible even after a
  // tap, so that focus is redone without one (Safari 18.4+; elsewhere focusVisible is ignored).
  settleDrawerFocus() {
    const drawer = document.querySelector('cart-drawer-component dialog[open]');
    if (!drawer) return;
    const visible = !!this.keyboardUsed;
    const active = document.activeElement;
    if (!drawer.contains(active)) {
      drawer.querySelector('button, a[href]')?.focus({ focusVisible: visible });
    } else if (!visible && active !== drawer && isFocusVisible(active)) {
      active.blur();
      active.focus({ focusVisible: false });
    }
  }

  fail(message, showBasketLink = false) {
    this.setPending(false);
    const { formError } = this.els;
    formError.replaceChildren(document.createTextNode(message));
    if (showBasketLink) {
      const link = document.createElement('a');
      link.href = themeRoutes().cart_url || '/cart';
      link.textContent = ' View basket';
      formError.append(link);
    }
    formError.hidden = false;
    // From the phone action bar the summary is off screen, so bring the message into view.
    const box = formError.getBoundingClientRect();
    if (box.top < 0 || box.bottom > window.innerHeight) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      formError.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    }
  }

  /*
   * A design in progress survives a reload (iOS reloads background tabs and in-app browsers): kept per
   * tab in sessionStorage, restored on the next load, and forgotten once added or on a new design. Storage
   * can be unavailable (private modes), so every access is guarded and the page works without it.
   */
  designStorageKey() {
    return `lae-bracelet-design:${this.config.productId}`;
  }

  // The design as it would be saved, or '' when there is nothing worth keeping. What the customer chose
  // only: whether they have looked at the styles yet is stored alongside but isn't a change to the design.
  designJson() {
    const { variantId, metal, stones, size, custom, note } = this.state;
    if (!stones.length && !size && !custom && !note) return '';
    return JSON.stringify({ variantId, metal, stones, size, custom, note });
  }

  // Stored with the address it belongs to (?variant), so a reload can tell this page's own not-yet-updated
  // address from a different link, and with the time, for the 24 h limit.
  storeDesign(json) {
    try {
      if (!json) sessionStorage.removeItem(this.designStorageKey());
      else {
        const link = new URLSearchParams(location.search).get('variant');
        const { styleConfirmed } = this.state;
        sessionStorage.setItem(this.designStorageKey(), JSON.stringify({ ...JSON.parse(json), styleConfirmed, link, savedAt: Date.now() }));
      }
    } catch (_error) {
      /* storage unavailable: the design simply isn't kept across a reload */
    }
  }

  saveDesign() {
    // The design just added stays out of storage, even after changes that end where they started.
    const json = this.designJson();
    const design = json === this.addedJson ? '' : json;
    if (design === this.savedJson) return;
    this.savedJson = design;
    this.storeDesign(design);
  }

  // A bad entry must never stop the designer loading: it is dropped instead.
  restoreDesign() {
    try {
      this.applySavedDesign();
    } catch (_error) {
      this.forgetDesign();
    }
  }

  applySavedDesign() {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(this.designStorageKey()) || 'null');
    } catch (_error) {
      return;
    }
    if (!saved || typeof saved !== 'object') return;
    const age = Date.now() - Number(saved.savedAt);
    if (!(age >= 0 && age <= DAY)) return;
    const { state, config, els } = this;
    const stones = (Array.isArray(saved.stones) ? saved.stones : []).filter((h) => this.stoneInfo.has(h)).slice(0, config.max);
    // A link that names a style (an ad, a shared link) wins over the saved style, but the address this page
    // had when the design was saved doesn't: the page's own ?variant update waits 1.2 s and may not have run.
    const urlVariant = new URLSearchParams(location.search).get('variant') || '';
    const linkWins = this.variants.has(urlVariant) && urlVariant !== saved.link;
    const savedVariant = this.variants.get(typeof saved.variantId === 'string' ? saved.variantId : '');
    const savedMetal = els.metalInputs.some((i) => i.value === saved.metal) ? saved.metal : null;
    if (!linkWins && savedVariant && savedVariant.available) {
      state.metal = savedVariant.metal;
      for (const input of els.metalInputs) input.checked = input.value === savedVariant.metal;
      // The address is left as it is: Meta's pixel would count a rewrite as another page view.
      this.setVariant(savedVariant.id, { writeUrl: false });
      if (saved.styleConfirmed) state.styleConfirmed = true;
    } else if (!linkWins && savedMetal) {
      // No usable saved style (none chosen in that metal yet, or it's unavailable now): keep the metal and
      // ask for a style, rather than quietly showing the default one.
      const metalName = config.metalNames[savedMetal].toLowerCase();
      state.metal = savedMetal;
      for (const input of els.metalInputs) input.checked = input.value === savedMetal;
      state.variantId = null;
      for (const input of els.styleInputs) input.checked = false;
      els.styleNotice.textContent = savedVariant
        ? `${savedVariant.label} in ${metalName} is unavailable right now. Choose another ${metalName} style below.`
        : `Choose one of the ${metalName} styles below.`;
      if (saved.styleConfirmed) state.styleConfirmed = true;
    }
    state.stones = stones;
    const sizes = new Set(els.sizeInputs.map((i) => i.value));
    if (sizes.has(saved.size)) state.size = saved.size;
    state.custom = typeof saved.custom === 'string' ? saved.custom.slice(0, 8) : '';
    els.customInput.value = state.custom;
    state.note = typeof saved.note === 'string' ? saved.note.slice(0, config.noteMax) : '';
    els.note.value = state.note;
    // Started (and perhaps completed) on the earlier page view: don't count the same design twice.
    this.tracked.started = true;
    this.tracked.completed = !this.firstProblem();
  }

  forgetDesign() {
    this.savedJson = '';
    try {
      sessionStorage.removeItem(this.designStorageKey());
    } catch (_error) {
      /* nothing stored */
    }
  }

  // After an add the design stays on screen, but a note typed now doesn't reach the bracelet in the basket.
  noteAfterAdd() {
    if (this.els.done.hidden || this.addedNote === undefined) return;
    const changed = (this.properties()[this.config.noteKey] || '') !== this.addedNote;
    if (changed === !!this.noteWarned) return;
    this.noteWarned = changed;
    const text = this.els.doneText;
    if (!changed) {
      text.textContent = this.doneMessage;
      return;
    }
    const email = document.createElement('a');
    email.href = 'mailto:lucy@laejewellery.com';
    email.textContent = 'lucy@laejewellery.com';
    text.replaceChildren(
      "This note isn't on the bracelet in your basket. Email it to me at ",
      email,
      ', or remove that bracelet from your basket and add this one again.'
    );
    this.announce("This note isn't on the bracelet in your basket. Email it to me, or remove that bracelet from your basket and add this one again.");
  }

  newDesign() {
    this.forgetDesign();
    this.addedJson = '';
    this.addedNote = undefined;
    this.noteWarned = false;
    this.incoming.clear();
    Object.assign(this.state, { stones: [], selected: -1, size: null, custom: '', note: '', showErrors: false });
    this.els.customInput.value = '';
    this.els.note.value = '';
    this.els.done.hidden = true;
    this.els.formError.hidden = true;
    this.tracked.completed = false;
    for (const toggle of this.els.monthToggles) this.closeMonth(document.getElementById(toggle.dataset.monthToggle));
    this.render();
    this.goToStep('2');
    this.announce('Started a new design. Your metal and style are kept.');
  }

  // Scrolling past the styles counts as having seen them: the action bar then goes straight on to the size.
  setupStyleSeen() {
    const grid = this.els.styleGrid;
    if (!grid || this.state.styleConfirmed || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        this.state.styleConfirmed = true;
        this.render();
      },
      { threshold: 0.35 }
    );
    io.observe(grid);
    this.abort.signal.addEventListener('abort', () => io.disconnect());
  }

  /* Gallery */

  setupGallery() {
    const { track, thumbs } = this.els;
    if (!track) return;
    const { signal } = this.abort;
    // Slides after the first arrive without their image addresses (see the section), so a phone gives the
    // first photo its whole connection. They load once the page has loaded, or as soon as the gallery is touched.
    const later = Array.from(track.querySelectorAll('img[data-src]'));
    if (later.length) {
      const load = () => {
        for (const img of later.splice(0)) {
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
          img.removeAttribute('data-srcset');
        }
      };
      const soon = () => (window.requestIdleCallback ? requestIdleCallback(load, { timeout: 2000 }) : setTimeout(load, 200));
      if (document.readyState === 'complete') soon();
      else window.addEventListener('load', soon, { once: true, signal });
      for (const type of ['pointerdown', 'touchstart', 'scroll', 'keydown', 'focusin']) {
        track.addEventListener(type, load, { once: true, passive: true, signal });
      }
      for (const thumb of thumbs) thumb.addEventListener('pointerdown', load, { once: true, passive: true, signal });
    }
    if (thumbs.length < 2) return;
    for (const thumb of thumbs) {
      thumb.addEventListener('click', () => this.showSlide(Number(thumb.dataset.thumb), true), { signal });
    }
    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.markThumb(Math.round(track.scrollLeft / track.clientWidth)));
      },
      { signal, passive: true }
    );
  }

  showSlide(index, smooth = false) {
    const { track } = this.els;
    if (!track) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: index * track.clientWidth, behavior: smooth && !reduce ? 'smooth' : 'auto' });
    this.markThumb(index);
  }

  markThumb(index) {
    for (const thumb of this.els.thumbs) {
      if (Number(thumb.dataset.thumb) === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    }
  }

  /* Mobile action bar: shown only when neither main action is on screen and nothing needs the space. */

  setupSticky() {
    const { sticky, start, add } = this.els;
    if (!sticky || !start || !add || !('IntersectionObserver' in window)) return;
    const { signal } = this.abort;
    this.mobile = window.matchMedia('(max-width: 989px)');
    this.visible = new Map();
    this.passedStart = false;
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.visible.set(entry.target, entry.isIntersecting);
        if (entry.target === start && !entry.isIntersecting && entry.boundingClientRect.top < 0) this.passedStart = true;
        if (entry.target === start && entry.isIntersecting) this.passedStart = false;
      }
      this.placeSticky();
    });
    this.observer.observe(start);
    this.observer.observe(add);
    sticky.hidden = false;
    sticky.classList.add('is-away');

    const later = () => requestAnimationFrame(() => this.placeSticky());
    document.addEventListener('focusin', later, { signal });
    this.addEventListener('focusin', (event) => this.keepFocusVisible(event.target), { signal });
    document.addEventListener('focusout', later, { signal });
    document.addEventListener('click', () => setTimeout(later, 400), { signal, passive: true });
    this.mobile.addEventListener('change', later, { signal });
  }

  // WCAG 2.4.11: browsers do not scroll a focused control out from under sticky UI, so do it here.
  keepFocusVisible(el) {
    if (this.autoScrolling || !el || this.els.strand.contains(el) || this.els.sticky.contains(el)) return;
    // Only keyboard focus needs moving clear of the strand and the bar. Safari never focuses a tapped button: it
    // focuses the step around it (a focus target for the Edit links), and keeping a whole step clear of the bar
    // threw the page about a screen down on every tap.
    if (Object.values(this.els.steps).includes(el) || !isFocusVisible(el)) return;
    requestAnimationFrame(() => {
      const r = el.getBoundingClientRect();
      let dy = 0;
      const strand = this.els.strand.getBoundingClientRect();
      const stuckAt = parseFloat(getComputedStyle(this.els.strand).top) || 0;
      // Covering: on screen at the top, pinned or being pushed off by the end of the stones step (once the
      // step is above the screen the strand is off it). After the scroll it pins again, so clear its full height.
      const covering = strand.bottom > 0 && strand.top <= stuckAt + 1;
      if (this.els.steps.stones.contains(el) && covering && r.top < strand.bottom + 8) dy = r.top - (stuckAt + strand.height) - 8;
      const bar = this.els.sticky;
      if (bar && !bar.classList.contains('is-away')) {
        const top = bar.getBoundingClientRect().top;
        if (r.bottom > top - 8) dy = r.bottom - top + 8;
      }
      if (dy) window.scrollBy({ top: dy, behavior: 'instant' });
    });
  }

  placeSticky() {
    const { sticky, start, add } = this.els;
    const active = document.activeElement;
    const typing = active && (active.matches('input[type="text"], textarea') || active.isContentEditable);
    // offsetParent is always null for fixed elements, so judge the consent banner by its geometry.
    const banner = document.querySelector('.shopify-pc__banner__dialog, #shopify-pc__banner');
    const box = banner?.getBoundingClientRect();
    const bannerShown = !!box && box.height > 0 && box.top < window.innerHeight && getComputedStyle(banner).visibility !== 'hidden';
    const dialogOpen = document.querySelector('dialog[open]');
    const show =
      this.mobile.matches &&
      this.passedStart &&
      !this.visible.get(start) &&
      !this.visible.get(add) &&
      !typing &&
      !bannerShown &&
      !dialogOpen;
    sticky.classList.toggle('is-away', !show);
    sticky.inert = !show;
  }

  /* Utilities */

  announce(text) {
    const el = this.els.announce;
    el.textContent = '';
    requestAnimationFrame(() => {
      el.textContent = text;
    });
  }

  track(name, data) {
    // Shopify's customer-events bus applies the store's consent rules. Never send notes or names.
    try {
      window.Shopify?.analytics?.publish?.(name, { template: TEMPLATE_ID, ...data });
    } catch (_error) {
      /* analytics must never break the purchase */
    }
  }
}

if (!customElements.get('lae-bracelet-builder')) {
  customElements.define('lae-bracelet-builder', LaeBraceletBuilder);
}

// Links in the help text, questions and small print open in a new tab, so a customer's design is kept.
const keepDesignOnLinks = () => {
  const links = document.querySelectorAll(
    '[data-lae-campaign] :is(.lae-help, .lae-faq__a, .lae-care, .lae-summary__small) a[href]'
  );
  for (const link of links) {
    if (link.target || /^(mailto:|tel:|#)/i.test(link.getAttribute('href'))) continue;
    link.target = '_blank';
    link.rel = 'noopener';
    link.insertAdjacentHTML('beforeend', '<span class="lae-sr"> (opens in a new tab)</span>');
  }
};

// Lucy's making clip: muted, plays only while mostly on screen, never on its own under reduced motion.
// The button pauses it for good (WCAG 2.2.2); without JavaScript the native controls stay.
const setupMakerVideos = () => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  for (const figure of document.querySelectorAll('[data-lae-campaign] [data-lae-video]')) {
    const video = figure.querySelector('video');
    const toggle = figure.querySelector('[data-lae-video-toggle]');
    if (!video || !toggle || figure.dataset.laeReady) continue;
    figure.dataset.laeReady = 'true';
    let held = still;
    video.controls = false;
    video.muted = true;
    toggle.hidden = false;
    const label = () => setText(toggle, video.paused ? 'Play video' : 'Pause video');
    video.addEventListener('play', label);
    video.addEventListener('pause', label);
    toggle.addEventListener('click', () => {
      held = !video.paused;
      if (held) video.pause();
      else video.play().catch(() => {});
    });
    label();
    if (!('IntersectionObserver' in window)) continue;
    new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !held) video.play().catch(() => {});
        else if (!entry.isIntersecting && !video.paused) video.pause();
      },
      { threshold: 0.5 }
    ).observe(video);
  }
};

// The script loads async, so the details section may still be arriving: run again once the page is parsed.
// Both skip what they have already set up.
keepDesignOnLinks();
setupMakerVideos();
if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      keepDesignOnLinks();
      setupMakerVideos();
    },
    { once: true }
  );
}
document.addEventListener('shopify:section:load', () => {
  keepDesignOnLinks();
  setupMakerVideos();
});
